import { getServerUrl } from '../../api/client';

/**
 * Base URL of the RMAI backend. Resolution order:
 *   1. localStorage `rmai-base-url` — dev / operator override.
 *   2. Derived from the EdgeWeb server URL as `<server>/rmai/api/v1`.
 *      Matches the demo-server IIS proxy rule that maps `/rmai/*` to the
 *      RMAI FastAPI service on localhost:8002 (see RMAI's DEPLOYMENT.md).
 *
 * For local dev against a RMAI instance on the same box, set:
 *   localStorage.setItem('rmai-base-url', 'http://localhost:8002/api/v1');
 */
export function getRmaiBaseUrl(): string {
  const override = localStorage.getItem('rmai-base-url');
  if (override) return override.replace(/\/$/, '');
  const server = getServerUrl().replace(/\/$/, '');
  return `${server}/rmai/api/v1`;
}

/**
 * RMAI labelling API key. Sourced from localStorage `rmai-api-key` (set
 * once per browser). The plugin refuses to open when the key is unset —
 * the picker renders a "how to set the key" hint instead of trying to
 * fetch algorithms and failing with a 401.
 *
 * Long-term this key belongs in the user's session (fetched from EdgeWeb's
 * auth backend), but for the first cut a localStorage entry is enough to
 * unblock the reader workflow.
 */
export function getRmaiApiKey(): string | null {
  const raw = localStorage.getItem('rmai-api-key');
  if (!raw) return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Reader identifier the wizard defaults to. Anonymised — 'reader-a',
 * 'reader-b', etc. Never a real name (RMAI spec §22 forbids that in the
 * exported dataset).
 *
 * Stored in localStorage so the same reader doesn't have to retype it
 * on every wizard open.
 */
export function getReaderId(): string {
  const raw = localStorage.getItem('rmai-reader-id');
  return raw && raw.trim().length > 0 ? raw.trim() : 'reader-a';
}

export function setReaderId(reader: string): void {
  const clean = reader.trim();
  if (clean) localStorage.setItem('rmai-reader-id', clean);
}
